import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import monitorsService from '../services/monitorsService';

// React Query hooks for the unified monitors API. Replaces what used to
// live in anomalyService + metricsService consumer hooks.
//
// Query key conventions:
//   ['monitors', connectionId, filters]            — list
//   ['monitor', connectionId, monitorId]           — single
//   ['monitor-values', connectionId, id, options]  — value history
//   ['monitor-alerts', connectionId, id, options]  — alerts for monitor
//   ['connection-alerts', connectionId, options]   — connection-wide
//   ['monitor-runs', connectionId, options]        — recent runs
//   ['monitor-metadata']                            — enum dropdowns
//
// Mutations invalidate the relevant query keys so the UI refreshes
// without page reload. createMonitor / updateMonitor / deleteMonitor
// invalidate ['monitors', connectionId]. runMonitor invalidates the
// affected monitor's value + alert lists.

// ------------------------------------------------------------------
// Queries
// ------------------------------------------------------------------

export const useMonitors = (connectionId, filters = {}, options = {}) => {
  return useQuery({
    queryKey: ['monitors', connectionId, filters],
    queryFn: () => monitorsService.listMonitors(connectionId, filters),
    enabled: !!connectionId,
    // Monitors don't change frequently — list refresh on user action
    // (create / delete) via invalidation, not polling.
    staleTime: 60_000,
    ...options,
  });
};

export const useMonitor = (connectionId, monitorId, options = {}) => {
  return useQuery({
    queryKey: ['monitor', connectionId, monitorId],
    queryFn: () => monitorsService.getMonitor(connectionId, monitorId),
    enabled: !!connectionId && !!monitorId,
    staleTime: 60_000,
    ...options,
  });
};

// Value history for a monitor (single segment if segment_value set, all
// segments if omitted). Used by the sparkline + history modal.
export const useMonitorValues = (connectionId, monitorId, options = {}) => {
  const { limit = 200, segment_value, ...queryOptions } = options;
  return useQuery({
    queryKey: ['monitor-values', connectionId, monitorId, { limit, segment_value }],
    queryFn: () =>
      monitorsService.getMonitorValues(connectionId, monitorId, { limit, segment_value }),
    enabled: !!connectionId && !!monitorId,
    staleTime: 30_000,
    ...queryOptions,
  });
};

export const useMonitorAlerts = (connectionId, monitorId, options = {}) => {
  const { status, limit = 100, ...queryOptions } = options;
  return useQuery({
    queryKey: ['monitor-alerts', connectionId, monitorId, { status, limit }],
    queryFn: () =>
      monitorsService.getMonitorAlerts(connectionId, monitorId, { status, limit }),
    enabled: !!connectionId && !!monitorId,
    staleTime: 30_000,
    ...queryOptions,
  });
};

// Connection-wide alert feed (across all monitors). Used by the
// Monitors page top-of-page alerts summary.
export const useConnectionAlerts = (connectionId, options = {}) => {
  const { status, severity, limit = 50, ...queryOptions } = options;
  return useQuery({
    queryKey: ['connection-alerts', connectionId, { status, severity, limit }],
    queryFn: () =>
      monitorsService.listConnectionAlerts(connectionId, { status, severity, limit }),
    enabled: !!connectionId,
    staleTime: 30_000,
    ...queryOptions,
  });
};

export const useConnectionRuns = (connectionId, options = {}) => {
  const { monitor_id, status, limit = 50, ...queryOptions } = options;
  return useQuery({
    queryKey: ['monitor-runs', connectionId, { monitor_id, status, limit }],
    queryFn: () =>
      monitorsService.listConnectionRuns(connectionId, { monitor_id, status, limit }),
    enabled: !!connectionId,
    staleTime: 30_000,
    ...queryOptions,
  });
};

// Enum dropdowns. Cached aggressively — backend only changes these via
// code deploy, so a long stale time saves a round-trip per form render.
export const useMonitorMetadata = (options = {}) => {
  return useQuery({
    queryKey: ['monitor-metadata'],
    queryFn: () => monitorsService.getMetadata(),
    staleTime: 5 * 60_000, // 5 min
    ...options,
  });
};

// ------------------------------------------------------------------
// Mutations
// ------------------------------------------------------------------

export const useCreateMonitor = (connectionId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => monitorsService.createMonitor(connectionId, payload),
    onSuccess: () => {
      // Refresh the monitor list — the new one should appear at the top
      // (backend orders by created_at DESC by default).
      queryClient.invalidateQueries({ queryKey: ['monitors', connectionId] });
    },
  });
};

export const useUpdateMonitor = (connectionId, monitorId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => monitorsService.updateMonitor(connectionId, monitorId, payload),
    onSuccess: () => {
      // Both the list and the individual monitor need refresh — list
      // cards might display config fields, and the edit form reads
      // the single-monitor query.
      queryClient.invalidateQueries({ queryKey: ['monitors', connectionId] });
      queryClient.invalidateQueries({ queryKey: ['monitor', connectionId, monitorId] });
    },
  });
};

export const useDeleteMonitor = (connectionId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (monitorId) => monitorsService.deleteMonitor(connectionId, monitorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['monitors', connectionId] });
    },
  });
};

// Manual trigger. Synchronous against the backend (Flask blocks on
// engine.run_monitor), so the spinner stays up until the warehouse
// query completes. After success we invalidate values + alerts for
// this monitor so the card refreshes with the new reading.
export const useRunMonitor = (connectionId, monitorId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => monitorsService.runMonitor(connectionId, monitorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['monitor-values', connectionId, monitorId] });
      queryClient.invalidateQueries({ queryKey: ['monitor-alerts', connectionId, monitorId] });
      queryClient.invalidateQueries({ queryKey: ['connection-alerts', connectionId] });
      queryClient.invalidateQueries({ queryKey: ['monitor-runs', connectionId] });
      // List query carries `last_collected_at` for each monitor — bump it.
      queryClient.invalidateQueries({ queryKey: ['monitors', connectionId] });
    },
  });
};

export const useUpdateAlertStatus = (connectionId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ alertId, status, resolutionNote }) =>
      monitorsService.updateAlertStatus(connectionId, alertId, status, resolutionNote),
    onSuccess: () => {
      // Invalidate every alerts query (don't know which monitor the
      // alert belongs to without parsing the response, and a single
      // ack/resolve is infrequent enough that broad invalidation is
      // cheap).
      queryClient.invalidateQueries({ queryKey: ['connection-alerts', connectionId] });
      queryClient.invalidateQueries({ queryKey: ['monitor-alerts', connectionId] });
    },
  });
};
